import { z } from "zod";

import { DEFAULT_LOCALE, DEFAULT_TRANSLATION_SETTINGS, SUPPORTED_LOCALES } from "../constants";

export const translationSettingsSchema = z.object({
  enabled: z.boolean(),
  fallbackLocale: z.literal(DEFAULT_LOCALE),
  locale: z.union([z.literal(SUPPORTED_LOCALES.enUs), z.literal(SUPPORTED_LOCALES.es419)]),
  updatedAt: z.string(),
  version: z.literal(1),
});

export type TranslationSettings = z.infer<typeof translationSettingsSchema>;

export function createDefaultTranslationSettings(): TranslationSettings {
  return {
    ...DEFAULT_TRANSLATION_SETTINGS,
    updatedAt: new Date().toISOString(),
  };
}

export function parseTranslationSettings(value: unknown): TranslationSettings {
  const parsed = translationSettingsSchema.safeParse(value);

  if (parsed.success) {
    return parsed.data;
  }

  const migratedSettings = migrateLegacyTranslationSettings(value);
  if (migratedSettings !== undefined) {
    return migratedSettings;
  }

  return createDefaultTranslationSettings();
}

function migrateLegacyTranslationSettings(value: unknown): TranslationSettings | undefined {
  const legacySettings = legacyTranslationSettingsSchema.safeParse(value);

  if (!legacySettings.success) {
    return undefined;
  }

  return {
    ...DEFAULT_TRANSLATION_SETTINGS,
    enabled: legacySettings.data.locale !== DEFAULT_LOCALE,
    locale: legacySettings.data.locale,
    updatedAt: legacySettings.data.updatedAt ?? new Date().toISOString(),
  };
}

const legacyTranslationSettingsSchema = z.object({
  locale: z.union([z.literal(SUPPORTED_LOCALES.enUs), z.literal(SUPPORTED_LOCALES.es419)]),
  updatedAt: z.string().optional(),
});
